import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Checkbox, FormControlLabel, Grid, List, ListItem, ListItemText, Switch, TextField } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { StyledComponentProps } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedClassKey, GlobalClassKeys } from "assets/jss/common-styles";
import classNames from "classnames";
import BoxDrawer from 'components/BoxDrawer';
import IconButtonCancel from 'components/Buttons/IconButtonCancel';
import { SpinnerIndicator } from 'components/CallServer';
import IconSearch from 'components/Icons/IconSearch';
import MuiRegularButton from 'components/MuiExtras/Button/MuiRegularButton';
import MuiMessageBox from 'components/MuiExtras/MessageBox/MuiMessageBox';
import MuiPaper from 'components/MuiExtras/MuiPaper';
import { ListType } from 'models/ApiEnums';
import { FeedItemInteractionDto, PublicationLookupFavoriteDto } from 'models/ApiModels';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    searchText: string;
    newListOpened: boolean;
    favoriteLoading: boolean;
    favoriteNewListIsPublic: boolean;
    favoriteNewListTitle: string;
    favoriteNewListError?: boolean;
    favoriteList: Array<PublicationLookupFavoriteDto>;
    lastError: any;
}

interface IProps {
    publicationId: string;
    onClose: () => void;
    onUpdate: (data: DeepPartial<FeedItemInteractionDto>, syncSidebar: boolean) => void;
}

type Props = IProps & StyledComponentProps<FeedClassKey> & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////////////////

class FavoritesDrawerComponent extends React.PureComponent<Props, IState> {
    private interval: any;
    private lastSearchText: string = "";
    private newListTextRef: HTMLInputElement;
    private searchTextRef: HTMLInputElement;

    constructor(props: Props) {
        super(props);
        this.state = {
            searchText: "",
            newListOpened: false,
            favoriteLoading: false,
            favoriteNewListIsPublic: false,
            favoriteNewListTitle: "",
            favoriteList: [],
            lastError: null
        };
    }

    ///
    private getFavorites(publicationId: string, searchText: string) {

        this.lastSearchText = searchText;

        this.setState({ favoriteLoading: true });

        api.getFavorites(publicationId, searchText)
            .then((result) => {
                this.setState({ favoriteList: result, favoriteLoading: false });
            })
            .catch((error) => {
                this.setState({ favoriteLoading: false, lastError: error });
            });
    }

    ///
    private postFavorite(listId: string, itemId: string, check: boolean) {

        this.setState({ favoriteLoading: true });

        api.postFavorite({ listId, itemId, check })
            .then((result) => {

                this.setState(
                    { favoriteList: result, favoriteLoading: false },
                    () => {
                        this.props.onUpdate({
                            inViewLaterList: result.filter(p => p.listType == ListType.SeeLater && p.check).length > 0,
                            favorited: result.filter(p => p.check).length > 0
                        }, false);
                    });

            })
            .catch(() => {
                this.setState({ favoriteLoading: false });
            });
    }

    ///
    private postNewFavoriteList(title: string, itemId: string, isPublic: boolean) {

        this.setState({ favoriteLoading: true });

        api.postNewFavoriteList({ itemId, title, isPublic })
            .then((result) => {

                this.setState(
                    {
                        newListOpened: false,
                        favoriteList: result,
                        favoriteLoading: false,
                        favoriteNewListTitle: "",
                        favoriteNewListIsPublic: false,
                        favoriteNewListError: null
                    },
                    () => {
                        this.props.onUpdate({ favorited: true }, true);
                        this.searchTextRef.focus();
                    }
                );

            })
            .catch((error) => {
                this.setState({ favoriteLoading: false });
            });
    }

    ///
    private handleFavoriteToggle(listId: string, itemId: string): void {
        const current = this.state.favoriteList.find(p => p.listId == listId);
        if (current != null)
            this.postFavorite(listId, itemId, !current.check);
    }

    ///
    private handleNewList = () => {
        this.postNewFavoriteList(this.state.favoriteNewListTitle, this.props.publicationId, this.state.favoriteNewListIsPublic);
    };

    ///
    componentDidMount() {
        this.getFavorites(this.props.publicationId, "");

        this.interval = setInterval(() => {
            if (this.state.searchText != this.lastSearchText && !this.state.favoriteLoading) {
                this.getFavorites(this.props.publicationId, this.state.searchText);
            }
        }, 3000);
    }

    ///
    componentWillUnmount() {
        clearInterval(this.interval);
    }

    ///
    public render() {
        const { publicationId, classes, onClose } = this.props;

        return (
            <BoxDrawer
                open
                title={this.props.t("Views.Timeline.Dialogs.FavoritesDrawer.BoxTitle")}
                onClose={onClose}
            >
                <Grid container direction="column" wrap="nowrap" style={{ height: "100%" }} spacing={1}>

                    <Grid item className={classNames(GlobalClassKeys.grow, classes.favoriteListContainer)}>

                        <MuiPaper dense elevation={0} fullHeight>

                            <TextField
                                autoFocus
                                fullWidth
                                inputRef={(ref) => this.searchTextRef = ref}
                                placeholder={this.props.t("Views.Timeline.Dialogs.FavoritesDrawer.InputPlaceholder")}
                                inputProps={{ maxLength: 255 }}
                                margin="normal"
                                value={this.state.searchText}
                                onChange={(e) => this.setState({ searchText: e.target.value })}
                                onKeyDown={
                                    (e) => {
                                        if (e.keyCode == 13) this.getFavorites(publicationId, this.state.searchText);
                                    }
                                }
                                InputProps={
                                    {
                                        startAdornment: <IconSearch fixedWidth />,
                                        endAdornment: this.lastSearchText.length > 0
                                            ? <IconButtonCancel
                                                size="small"
                                                onClick={
                                                    () => {
                                                        this.setState({ searchText: "" }, () => this.getFavorites(this.props.publicationId, ""));
                                                    }
                                                } />
                                            : null
                                    }
                                }
                            />

                            <List disablePadding dense>
                                {
                                    this.state.lastError
                                        ? <MuiMessageBox variant="error" message={this.props.t("Views.Timeline.Dialogs.FavoritesDrawer.GetListError")} />
                                        : this.state.favoriteLoading && this.state.favoriteList.length == 0
                                            ? <SpinnerIndicator />
                                            : this.state.favoriteList.map(value => (
                                                <ListItem
                                                    button
                                                    key={value.listId}
                                                    disabled={this.state.favoriteLoading}
                                                    disableGutters
                                                    onClick={() => this.handleFavoriteToggle(value.listId, publicationId)}
                                                >
                                                    <Checkbox
                                                        className={this.props.classes.checkboxList}
                                                        tabIndex={-1}

                                                        checked={
                                                            this.state.favoriteList.findIndex(p => p.listId == value.listId && p.check) !== -1
                                                        }
                                                    />
                                                    <ListItemText primary={value.title} />
                                                </ListItem>
                                            ))
                                }
                            </List>
                        </MuiPaper>

                    </Grid>

                    <Grid item>
                        <Accordion
                            expanded={this.state.newListOpened}
                            disabled={this.state.favoriteLoading}
                            elevation={0}
                            onChange={
                                (e, expanded) => {
                                    this.setState({ newListOpened: expanded },
                                        () => {
                                            if (expanded) this.newListTextRef.focus();
                                        });
                                }
                            }
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <span>CRIAR NOVA LISTA</span>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container direction="column">
                                    <Grid item>
                                        <TextField
                                            autoFocus
                                            fullWidth
                                            placeholder={this.props.t("Common.Input.Placeholder.Name")}
                                            inputRef={(ref) => this.newListTextRef = ref}
                                            inputProps={{ maxLength: 255 }}
                                            error={this.state.favoriteNewListError}
                                            value={this.state.favoriteNewListTitle}
                                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                var v = event.target.value;
                                                if (v.length == 0) this.setState({ favoriteNewListError: true });
                                                else this.setState({ favoriteNewListTitle: v });
                                            }}
                                            onKeyDown={
                                                (e) => {
                                                    if (e.keyCode == 13) this.handleNewList();
                                                }
                                            }
                                        />
                                    </Grid>
                                    <Grid item>
                                        <Grid container justifyContent="space-between" direction="row">
                                            <Grid item>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            color="secondary"
                                                            checked={this.state.favoriteNewListIsPublic}
                                                            onChange={() => {
                                                                this.setState({ favoriteNewListIsPublic: !this.state.favoriteNewListIsPublic });
                                                            }}
                                                        />
                                                    }
                                                    label="Pública"
                                                />
                                            </Grid>
                                            <Grid item  >
                                                <MuiRegularButton
                                                    color="secondary"
                                                    disabled={this.state.favoriteNewListTitle.length == 0}
                                                    onClick={this.handleNewList}
                                                >
                                                    <span>{this.props.t("Common.Buttons.CreateText")}</span>
                                                </MuiRegularButton>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </Grid>

                </Grid>
            </BoxDrawer >
        );
    }
}

export default withTranslation()(FavoritesDrawerComponent);
